// Signed monetary delta with three states (positive / negative /
// non-financial). Per brand SKILL.md §9.4: explicit sign for non-zero,
// proper Unicode minus (U+2212) — not the ASCII hyphen — and an
// `aria-label` announcing "positive impact" / "negative impact" plus the
// absolute value plus the GST basis for screen readers (the visual + / −
// glyphs are not always read aloud reliably).
//
// Reusable for any signed monetary delta on a contract surface — the
// GST basis prop is required (no implicit default) because every dollar
// value on a contract surface must show its basis to be contractually
// safe.

import { gstSuffix } from "../../../../utils/gstSuffix";

const UNICODE_MINUS = "−";

export interface ValueImpactCellProps {
  /** AUD amount with up to 2 decimal places, or null for a non-financial
   *  variation. The cell renders the value with cents precision so totals
   *  reconcile against contract documents down to the cent. */
  value: number | null;
  gstApplies: boolean | undefined;
  /** Test hook applied to the rendered span. */
  testId?: string;
}

function formatAud(amount: number): string {
  return `$${amount.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatZero(): string {
  return "$0.00";
}

export default function ValueImpactCell({
  value,
  gstApplies,
  testId,
}: ValueImpactCellProps) {
  if (value === null) {
    return (
      <span
        className="text-body-sm text-slate-400 dark:text-slate-500"
        data-testid={testId}
      >
        {"—"} non-financial
      </span>
    );
  }

  const basis = gstSuffix(gstApplies);

  if (value > 0) {
    // Positive deltas render in default text colour, not emerald. A green
    // "+$50,000" reads as good news, but a contract scope-up has no inherent
    // valence — it might be welcome budget, or scope creep, or a costed
    // change-order the client must fund. The `+` glyph already encodes the
    // sign; colour stays reserved for negatives (descopes) where red is
    // contractually meaningful.
    return (
      <span
        className="text-body-sm font-medium tabular-nums text-slate-900 dark:text-slate-100"
        aria-label={`positive impact, ${formatAud(value)} ${basis}`}
        data-testid={testId}
      >
        +{formatAud(value)}{" "}
        <span className="text-caption font-normal text-slate-500 dark:text-slate-400">
          {basis}
        </span>
      </span>
    );
  }

  if (value < 0) {
    return (
      <span
        className="text-body-sm font-medium tabular-nums text-red-600 dark:text-red-400"
        aria-label={`negative impact, ${formatAud(Math.abs(value))} ${basis}`}
        data-testid={testId}
      >
        {UNICODE_MINUS}
        {formatAud(Math.abs(value))}{" "}
        <span className="text-caption font-normal text-slate-500 dark:text-slate-400">
          {basis}
        </span>
      </span>
    );
  }

  // Explicit zero — distinct from non-financial null. Rare but possible
  // (offsetting line items netting to zero).
  return (
    <span
      className="text-body-sm tabular-nums text-slate-700 dark:text-slate-200"
      data-testid={testId}
    >
      {formatZero()}{" "}
      <span className="text-caption font-normal text-slate-500 dark:text-slate-400">
        {basis}
      </span>
    </span>
  );
}
